import { OnDestroy, Output, EventEmitter, Input, ViewChild, Directive, inject } from "@angular/core";
import { LinkableComponent } from "@app/shared/linkable/linkable.component";
import { IHasGuid } from "@app/shared/interfaces/IHasGuid";
import { Observable, Subscription } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { finalize } from "rxjs/operators";
import { CerrixPromptService } from "../services/cerrix-prompt.service";
import { RendererConfig } from "@models/workspace/RendererConfig";

@Directive()
export abstract class ItemLinkableBase implements OnDestroy {
    private _linkableSubscriptions = new Subscription();

    @ViewChild(LinkableComponent)
    set linkableComponent(component: LinkableComponent | undefined) {
        this._linkableSubscriptions.unsubscribe();
        this._linkableSubscriptions = new Subscription();

        if (!component) {
            return;
        }

        if (this.linkedChange.observers.length > 0) {
            this._linkableSubscriptions.add(
                component.linkedChange.subscribe((x) => {
                    this.linkedChange.emit(x);
                })
            );
        }

        if (this.onBatchClicked.observers.length > 0) {
            this._linkableSubscriptions.add(
                component.onBatchClicked.subscribe((x) => {
                    this.onBatchClicked.emit(x);
                })
            );
        }

        if (this.onLinkedClick.observers.length > 0) {
            this._linkableSubscriptions.add(
                component.onLinkedClick.subscribe((x) => {
                    this.onLinkedClick.emit(x);
                })
            );
        }

        if (this.onAddClicked.observers.length > 0) {
            this._linkableSubscriptions.add(
                component.onAddClicked.subscribe((x) => {
                    this.onAddClicked.emit(x);
                })
            );
        }

        if (this.onReloadClicked.observers.length > 0) {
            this._linkableSubscriptions.add(
                component.onReloadClicked.subscribe((x) => {
                    this.onReloadClicked.emit(x);
                })
            );
        }
    }

    @Output() linkedChange = new EventEmitter<any[]>();
    @Output() onLinkedClick: EventEmitter<any> = new EventEmitter();
    @Output() onBatchClicked: EventEmitter<any> = new EventEmitter();
    @Output() onOpenClicked: EventEmitter<any> = new EventEmitter();
    @Output() onAddClicked: EventEmitter<any> = new EventEmitter();
    @Output() onReloadClicked: EventEmitter<any> = new EventEmitter();

    @Input() linked: IHasGuid[];
    @Input() unlinked: IHasGuid[];
    @Input() displayValueProp: string;
    @Input() hideActions = false;
    @Input() hideFilter = false;
    @Input() orderByValueProperty = false;
    @Input() disabled = false;
    @Input() allowAddNew = false;
    @Input() rendererConfig: RendererConfig[];

    // The inner LinkableComponent only shows these actions when the consumer of this
    // wrapper binds the matching output. Bind these in the wrapper template, because a
    // template binding is read on each change detection cycle.
    get allowEditItem(): boolean {
        return this.onLinkedClick.observers.length > 0;
    }

    get allowBatchEdit(): boolean {
        return this.onBatchClicked.observers.length > 0;
    }

    hasAccessChecks = {};
    accessCheckInProgress = false;

    protected _toastr = inject(ToastrService);
    protected _promptService = inject(CerrixPromptService);

    abstract hasAccessToItem(guid: string): Observable<boolean>;
    abstract openTab(item: any, hasAccess: boolean);

    ngOnDestroy(): void {
        this._linkableSubscriptions.unsubscribe();
    }

    openItem(item: IHasGuid) {
        const hasAccess = this.hasAccessChecks[item.guid];
        if (hasAccess == null && !this.accessCheckInProgress) {
            this.accessCheckInProgress = true;

            const prompt = this._promptService.loader("Checking rights, please wait...");

            this.hasAccessToItem(item.guid)
                .pipe(finalize(() => prompt.close()))
                .subscribe(
                    (x) => {
                        this.hasAccessChecks[item.guid] = x;

                        this.openTab(item, x);
                        this.accessCheckInProgress = false;
                    },
                    (error) => {
                        this.accessCheckInProgress = false;
                    }
                );
        } else {
            this.openTab(item, hasAccess);
        }
    }

    reloadClicked() {
        if (this.onReloadClicked.observers.length > 0) {
            this.onReloadClicked.emit();
        }
    }
}
